import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, CheckCircle, Shield, Clock, Users, ArrowRight, Car, FileText, Stethoscope, Scale, AlertTriangle, ExternalLink } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const EverettCarAccidentPage = () => {
  // FAQ data for schema
  const faqData = [
    {
      question: "How much does an Everett car accident lawyer cost?",
      answer: "I work on contingency - you pay nothing unless I win your case. No upfront fees, no hourly billing. My fee comes from the settlement, so you can focus on healing without financial stress."
    },
    {
      question: "Should I talk to the insurance company after my Everett car accident?",
      answer: "No. Insurance adjusters are trained to minimize your claim. Let me handle all insurance communication. I used to represent insurance companies, so I know their tactics and how to counter them."
    },
    {
      question: "How long do I have to file a car accident claim in Washington?",
      answer: "Washington has a 3-year statute of limitations, but don't wait. Evidence disappears, witnesses forget, and insurance companies use delays against you. Contact me immediately for the strongest case."
    },
    {
      question: "What is my Everett car accident case worth?",
      answer: "Every case is different. Value depends on injury severity, medical treatment, lost wages, pain and suffering, and available insurance coverage. I'll give you an honest assessment in a free consultation."
    },
    {
      question: "Do I need a lawyer for a car accident in Everett?",
      answer: "If you have significant injuries or the insurance company is giving you trouble, yes. I handle the paperwork, negotiate with insurance, and fight for maximum compensation while you focus on recovery."
    }
  ];

  const processSteps = [
    {
      step: 1,
      title: "Free Consultation",
      description: "Call or text me directly. I'll listen to your story and give you honest advice - even if that means you don't need a lawyer.",
      icon: Phone
    },
    {
      step: 2,
      title: "I Stop the Insurance Calls",
      description: "Once you hire me, insurance companies talk to me, not you. No more stressful calls or pressure tactics.",
      icon: Shield
    },
    {
      step: 3,
      title: "You Focus on Healing",
      description: "Get the medical treatment you need. I'll handle the legal work while you recover.",
      icon: Stethoscope
    },
    {
      step: 4,
      title: "I Build Your Case",
      description: "I gather evidence, document your injuries, calculate your damages, and build the strongest possible claim.",
      icon: FileText
    },
    {
      step: 5,
      title: "You Decide",
      description: "I negotiate maximum compensation. You decide whether to accept a settlement or go to trial. Your case, your choice.",
      icon: Scale
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="everett-car-accident-page">
      <SEOHead 
        title="Everett Car Accident Lawyer | Free Consultation | Amy C. Brown Law"
        description="Injured in a car accident in Everett? I take over the insurance fight so you can focus on healing. Former insurance defense attorney. No fees unless you win. Call (425) 675-1224."
        faqData={faqData}
        localArea={{ city: "Everett", county: "Snohomish", state: "WA" }}
        serviceData={{
          name: "Everett Car Accident Legal Representation",
          description: "Legal representation for car accident victims in Everett, Washington. Specializing in insurance negotiations, whiplash injuries, rear-end collisions, and maximum compensation recovery.",
          category: "Personal Injury Law - Auto Accidents"
        }}
      />

      {/* Hero Section */}
      <section className="pt-36 pb-16 md:pt-40 md:pb-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Car className="w-4 h-4" />
              Everett Car Accident Attorney
            </div>
            
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-secondary mb-6 leading-tight">
              Injured in an Everett Car Crash?
            </h1>
            
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              After a car crash, the stress of dealing with insurance companies and medical bills can feel overwhelming. I've helped countless Everett residents in your exact position. Whether your accident happened on I-5, Highway 2, or a neighborhood street, I'm here to help.
            </p>

            <div className="flex flex-wrap gap-4">
              <a 
                href="tel:425-675-1224"
                className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold hover:bg-primary/90 transition-all"
                data-testid="hero-call-btn"
              >
                <Phone className="w-5 h-5" />
                Call (425) 675-1224
              </a>
              <a 
                href="sms:425-675-1224"
                className="inline-flex items-center justify-center gap-2 border-2 border-secondary text-secondary px-8 py-4 rounded-full font-semibold hover:bg-secondary hover:text-white transition-all"
                data-testid="hero-text-btn"
              >
                <MessageCircle className="w-5 h-5" />
                Text Me
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-secondary mb-4">
              Insurance Companies Calling, Medical Bills Arriving, Nothing But Questions
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              I understand what you're going through. I've guided countless people through this chaos.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: AlertTriangle, title: "The Adjuster Keeps Calling", desc: "They want a recorded statement. They sound friendly, but they're trained to find reasons to pay you less." },
              { icon: FileText, title: "Bills Are Piling Up", desc: "Hospital, doctors, specialists—the bills keep coming while you're trying to recover and can't work." },
              { icon: Clock, title: "You Don't Know What to Do", desc: "How long do you have? What should you say? What shouldn't you sign? It's overwhelming." },
              { icon: Car, title: "Their Offer Seems Low", desc: "You sense the insurance company's offer doesn't cover what you're really going through." }
            ].map((item, index) => (
              <div key={index} className="bg-slate-50 p-6 rounded-xl border border-slate-100">
                <item.icon className="w-10 h-10 text-primary mb-4" />
                <h3 className="font-heading font-semibold text-secondary mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-accent-soft rounded-2xl p-8 border-l-4 border-primary max-w-3xl mx-auto">
            <p className="text-slate-700 leading-relaxed">
              I'm more than an attorney—<strong>I've experienced trauma myself.</strong> Multiple members of my family were killed in a construction-related incident. I vividly remember that horrible day and the days after. I know pain, and I understand the pain you're feeling right now.
            </p>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-secondary mb-4">
              How Your Case Works
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Clear steps, no surprises. You'll always know what's happening with your case.
            </p>
          </div>

          <div className="grid md:grid-cols-5 gap-6">
            {processSteps.map((step) => (
              <div key={step.step} className="relative">
                <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 h-full">
                  <div className="bg-primary text-white w-8 h-8 rounded-full flex items-center justify-center font-bold mb-4">
                    {step.step}
                  </div>
                  <step.icon className="w-8 h-8 text-primary mb-3" />
                  <h3 className="font-heading font-semibold text-secondary mb-2">{step.title}</h3>
                  <p className="text-slate-600 text-sm">{step.description}</p>
                </div>
                {step.step < 5 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 text-slate-300">
                    <ArrowRight className="w-6 h-6" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl font-bold text-secondary mb-6">
                Why Clients Throughout Snohomish County Choose Me
              </h2>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="bg-primary/10 p-3 rounded-full h-fit">
                    <Shield className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-secondary mb-1">I've Seen the Other Side</h3>
                    <p className="text-slate-600">Having started my career defending employers and insurance companies, I gained a rare understanding of their strategies. Now I use that insight to fight for you.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="bg-primary/10 p-3 rounded-full h-fit">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-secondary mb-1">You Work Directly With Me</h3>
                    <p className="text-slate-600">I personally handle your case. When you call, you reach me—not a secretary or paralegal. Your case is too important for hand-offs.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="bg-primary/10 p-3 rounded-full h-fit">
                    <Clock className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-secondary mb-1">You'll Never Be Left in the Dark</h3>
                    <p className="text-slate-600">One of the most common frustrations clients have with lawyers is lack of updates. I'm committed to keeping you fully informed every step of the way.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="bg-primary/10 p-3 rounded-full h-fit">
                    <Scale className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-secondary mb-1">No Fee Unless You Win</h3>
                    <p className="text-slate-600">You're already dealing with medical bills. My fee comes from your settlement—you pay nothing out of pocket.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 p-8 rounded-2xl">
              <h3 className="font-heading text-2xl font-bold text-secondary mb-6">
                Let's Talk About Your Accident
              </h3>
              <p className="text-slate-600 mb-6">
                Not sure if you have a case? Call or text me. I'll listen, explain your options clearly, and give you honest advice—even if that means you don't need a lawyer.
              </p>
              <div className="space-y-4">
                <a 
                  href="tel:425-675-1224"
                  className="flex items-center justify-center gap-2 bg-primary text-white px-6 py-4 rounded-full font-semibold hover:bg-primary/90 transition-all w-full"
                >
                  <Phone className="w-5 h-5" />
                  Call (425) 675-1224
                </a>
                <a 
                  href="sms:425-675-1224"
                  className="flex items-center justify-center gap-2 border-2 border-secondary text-secondary px-6 py-4 rounded-full font-semibold hover:bg-secondary hover:text-white transition-all w-full"
                >
                  <MessageCircle className="w-5 h-5" />
                  Text Me Now
                </a>
              </div>
              <p className="text-sm text-slate-500 mt-4 text-center">
                I respond personally within 1 business day
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-secondary mb-4">
              Common Questions About Everett Car Accidents
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {faqData.map((faq, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-sm">
                <h3 className="font-heading font-semibold text-secondary mb-3">{faq.question}</h3>
                <p className="text-slate-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Concepts Section - Entity SEO */}
      <section className="py-12 bg-white border-t border-slate-100">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <h2 className="font-heading text-xl font-semibold text-secondary mb-6 text-center">
            Understanding Car Accident Law
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <a href="https://en.wikipedia.org/wiki/Traffic_collision" target="_blank" rel="noopener noreferrer" className="bg-slate-50 p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Traffic Collision <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Whiplash_(medicine)" target="_blank" rel="noopener noreferrer" className="bg-slate-50 p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Whiplash Injury <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Personal_injury" target="_blank" rel="noopener noreferrer" className="bg-slate-50 p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Personal Injury <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Contingent_fee" target="_blank" rel="noopener noreferrer" className="bg-slate-50 p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Contingency Fee <ExternalLink className="w-3 h-3" />
              </span>
            </a>
          </div>
          <p className="text-xs text-slate-400 text-center mt-4">
            Learn more about these legal concepts on Wikipedia
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-secondary">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-3xl font-bold text-white mb-6">
            Don't Face the Insurance Company Alone
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            You focus on healing. I'll handle the insurance fight.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:425-675-1224"
              className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-primary/90 transition-all"
            >
              <Phone className="w-5 h-5" />
              Call (425) 675-1224
            </a>
            <a 
              href="sms:425-675-1224"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/10 transition-all"
            >
              <MessageCircle className="w-5 h-5" />
              Text Me Now
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EverettCarAccidentPage;
